import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import jobStatuses from './constants/job-statuses';

function SettingsJobStatusColors() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');

  const colors = jobStatuses.COLOUR_NAMES;
  const icons = ['📋', '📅', '🚗', '🔧', '⏸️', '✅', '💰', '🚫', '⚙️', '❌', '⚠️', '📦', '🏗️', '📞'];

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    const res = await apiFetch('/api/job-status-colors');
    const data = await res.json().catch(() => null);
    if (!res.ok || !Array.isArray(data)) {
      setStatuses([]);
      setMessage((data && data.error) || 'Could not load status colours');
      return;
    }
    setMessage('');
    setStatuses(data);
  };

  const handleSave = async (status) => {
    const body = {
      ...status,
      timeline_color: status.display_color,
    };
    const res = await apiFetch(`/api/job-status-colors/${status.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const saved = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(saved.error || 'Could not save this status colour');
      return;
    }
    setEditing(null);
    fetchStatuses();
  };

  const handleReset = async (id) => {
    if (!window.confirm('Reset this colour to the standard one?')) return;
    const res = await apiFetch(`/api/job-status-colors/${id}`, { method: 'DELETE' });
    const saved = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(saved.error || 'Could not reset this status colour');
      return;
    }
    setEditing(null);
    fetchStatuses();
  };

  const colorClasses = Object.fromEntries(
    jobStatuses.COLOUR_NAMES.map((name) => [name, jobStatuses.COLOUR_SWATCHES[name].chip])
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/settings/dispatch')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
              title="Back to Settings"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">Job Status Colors</h1>
              <span className="text-sm text-blue-100">Colours for the eight job statuses on this company</span>
            </div>
            <div style={{ width: '100px' }}></div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-white rounded-lg shadow-md border border-gray-300 p-6">
          <p className="text-gray-600 mb-4">
            The status names are fixed. A colour saved here is used on Engineer Dispatch, the job list and the job page for this company. Reset puts the standard colour back.
          </p>
          {message ? <p className="text-red-700 mb-4">{message}</p> : null}

          <table className="w-full" data-testid="job-status-colours">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Colour</th>
                <th className="text-left p-3">Icon</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((status) => {
                const label = jobStatuses.displayLabel(status.status_name);
                const isEditing = editing && editing.id === status.id;
                const shownColour = isEditing ? editing.display_color : (status.timeline_color || status.display_color);
                const shownIcon = isEditing ? editing.icon : status.icon;
                return (
                  <tr key={status.id} className="border-b border-gray-200 hover:bg-gray-50" data-status={status.status_name}>
                    <td className="p-3 font-medium">{label}</td>
                    <td className="p-3">
                      {isEditing ? (
                        <select
                          aria-label={`Colour for ${label}`}
                          value={editing.display_color}
                          onChange={(e) => setEditing({ ...editing, display_color: e.target.value })}
                          className="px-3 py-2 border rounded w-full"
                        >
                          {colors.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : (
                        <span className={`px-3 py-1 rounded-full border-2 ${colorClasses[shownColour] || ''}`}>
                          {shownColour}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-2xl">
                      {isEditing ? (
                        <select
                          aria-label={`Icon for ${label}`}
                          value={editing.icon}
                          onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                          className="px-3 py-2 border rounded text-xl"
                        >
                          {icons.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                      ) : shownIcon}
                    </td>
                    <td className="p-3 text-right space-x-2">
                      {isEditing ? (
                        <>
                          <button type="button" onClick={() => handleSave(editing)} className="px-3 py-1 bg-green-600 text-white rounded">Save</button>
                          <button type="button" onClick={() => setEditing(null)} className="px-3 py-1 bg-gray-400 text-white rounded">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => setEditing({ ...status, display_color: shownColour })} className="px-3 py-1 bg-blue-600 text-white rounded">Edit</button>
                          <button type="button" onClick={() => handleReset(status.id)} className="px-3 py-1 bg-gray-600 text-white rounded">Reset</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SettingsJobStatusColors;
